import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  ListItemSecondaryAction,
  Typography,
  Box,
  Button,
  DialogActions,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import { cloneDeep, remove } from 'lodash';
import { updatePatient } from './_api';
import useAlerts from 'components/Alerts/useAlerts';
import { useAppGlobals } from 'utils/hooks/useAppGlobals';
import ConfirmDialog from 'components/ConfirmationDialog/ConfirmationDialog';

interface PractitionerManageDialogProps {
  open: boolean;
  onClose: () => void;
  practitioners: any[];
  patient: any;
  onChanged: () => void;
}

const PractitionerManageDialog: React.FC<PractitionerManageDialogProps> = ({
  open,
  onClose,
  practitioners,
  patient,
  onChanged,
}) => {
  const { t } = useTranslation(['Patients', 'Common']);
  const { addErrorAlert, addSuccessAlert } = useAlerts();
  const { toggleLoader } = useAppGlobals();

  const [practitionerToRemove, setPractitionerToRemove] = useState<any | null>(null);

  const handleRemove = async () => {
    if (!practitionerToRemove || !patient) return;

    toggleLoader(true);
    try {
      const updatedPatient = cloneDeep(patient);
      const currentIds = updatedPatient.generalPractitionerIds || [];

      remove(currentIds, (id: number) => id === practitionerToRemove.id);
      updatedPatient.generalPractitionerIds = currentIds;

      const result = await updatePatient(updatedPatient);
      if (result) {
        addSuccessAlert(t('Patients:practitionerRemoved'));
        onChanged();
        setPractitionerToRemove(null);
      } else {
        addErrorAlert(t('Patients:updatePatientError'));
      }
    } catch (error) {
      console.error(error);
      addErrorAlert(t('Patients:updatePatientError'));
    } finally {
      toggleLoader(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ m: 0, p: 2 }}>
          {t('Patients:managePractitioners')}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {practitioners.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('Patients:noPractitioners')}</Typography>
            </Box>
          ) : (
            <List>
              {practitioners.map((practitioner) => (
                <ListItem key={practitioner.id}>
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={practitioner.nameTranslated}
                    secondary={
                      <>
                        {practitioner.telecomTranslated !== '-' && (
                          <Typography component="span" variant="body2" display="block">
                            {practitioner.telecomTranslated}
                          </Typography>
                        )}
                        {practitioner.addressTranslated !== '-' && (
                          <Typography component="span" variant="body2" display="block">
                            {practitioner.addressTranslated}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title={t('Patients:disconnectPractitioner')}>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => setPractitionerToRemove(practitioner)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            {t('Buttons:close')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!practitionerToRemove}
        title={t('Patients:removePractitioner')}
        confirmAction={handleRemove}
        cancelAction={() => setPractitionerToRemove(null)}
        onClose={() => setPractitionerToRemove(null)}
      >
        <Typography>
          {t('Patients:removePractitionerConfirm', {
            name: practitionerToRemove?.nameTranslated,
          })}
        </Typography>
      </ConfirmDialog>
    </>
  );
};

export default PractitionerManageDialog;
